import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { EmptyState } from "./EmptyState";

// The shared cross-link pattern (Session AQW, Phase 2 record pages). Every record page links to
// related records -- a quote to its customer/site, a customer to its sites/lifts/contracts/quotes,
// a lift to its covering contract -- and before this session each of those was a one-off: a plain
// `<Link>`, a `<dt>/<dd>` row, a custom list item, each styled slightly differently
// (SiteDetailPage's contract row, CustomerDetailPage's sites list, ContractDetailPage's lift
// coverage links). One look for "here is a related record", used consistently across every page
// this session touches or adds.
//
// RecordLink is the single reference (one related record, one row). RecordLinkGroup is the
// "N of these, or none yet" wrapper every page needs around a list of them -- it owns the heading,
// the count and the EmptyState fallback so a caller never re-derives "is this list actually empty"
// styling by hand.

export interface RecordLinkProps {
  to: string;
  label: ReactNode;
  meta?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}

export function RecordLink({ to, label, meta, trailing, className = "" }: RecordLinkProps) {
  return (
    <Link
      to={to}
      className={`flex items-center justify-between gap-3 rounded-ele border border-ele-hairline bg-ele-surface px-3 py-2 text-sm transition hover:border-ele-purple-accent hover:bg-ele-inset ${className}`.trim()}
    >
      <span className="min-w-0">
        <span className="block truncate text-ele-strong">{label}</span>
        {meta != null && <span className="mt-0.5 block truncate text-xs text-ele-body opacity-70">{meta}</span>}
      </span>
      {trailing != null && <span className="flex-none">{trailing}</span>}
    </Link>
  );
}

export interface RecordLinkGroupProps {
  title: string;
  count?: number;
  children: ReactNode;
  empty?: string;
  action?: ReactNode;
  className?: string;
}

// React renders nothing for null/undefined/boolean children -- exactly what `{cond && <RecordLink/>}`
// produces when `cond` is false (`false`, not "absent"). A caller building the list from several
// such expressions (every real-world caller: RecordHubPage-style pages list several OPTIONAL
// cross-links this way) therefore always passes an ARRAY, and a naive `children.length > 0` counts
// those false entries as content -- showing an empty, click-nothing box instead of the EmptyState
// message when every condition is false. Filter to what will actually render.
function isRenderableChild(c: ReactNode): boolean {
  return c != null && c !== false && c !== true;
}

export function RecordLinkGroup({ title, count, children, empty, action, className = "" }: RecordLinkGroupProps) {
  const hasChildren = Array.isArray(children) ? children.some(isRenderableChild) : isRenderableChild(children);
  return (
    <section className={className}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="font-heading text-lg tracking-wide text-ele-strong">
          {title}
          {count != null ? ` (${count})` : ""}
        </h2>
        {action}
      </div>
      {hasChildren ? (
        <div className="space-y-1.5">{children}</div>
      ) : (
        <EmptyState message={empty ?? `No ${title.toLowerCase()} yet.`} />
      )}
    </section>
  );
}
